/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import { Button } from 'react-bootstrap';
import { EMPTY_LIST_PLACEHOLDER } from 'utils/values';
import CountUp from 'react-countup';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import DateRangePicker from 'common/date-range-picker';
import AnalyticsChartContainer, { getDefaultChartOptions } from 'common/analytics-chart-container';
import moment from 'moment';
import axios from 'axios';
import { LMS_BASE_URL } from 'config';
import 'utils/chartSetup';
import {
  MOCK_TOP_COHORTS_RESPONSE,
  USE_ANALYTICS_MOCK_DATA,
} from 'analytics/analyticsMockData';
import { Block } from '../components/stats/styled';

const HIGHLIGHT_REVENUE_COLOR = 'rgb(255, 193, 7)';
const HIGHLIGHT_ENROLLMENT_COLOR = 'rgb(40, 167, 69)';
const ENROLLMENT_BAR_COLOR = 'rgba(57, 91, 135, 0.85)';
const REVENUE_BAR_COLOR = 'rgba(255, 127, 80, 0.75)';

const truncateLabel = (label, maxLength = 28) => (
  label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label
);

const formatCurrency = (amount, currency) => (
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'PKR',
    maximumFractionDigits: 0,
  }).format(amount || 0)
);

const renderHighlightStatValue = (stat, currency) => {
  if (!(stat.value > 0)) {
    return EMPTY_LIST_PLACEHOLDER;
  }
  if (stat.isCurrency) {
    return formatCurrency(stat.value, stat.cohort?.currency || currency);
  }
  return <CountUp end={stat.value} separator="," />;
};

const buildGraphStateFromResponse = (data) => {
  const cohortRows = data?.cohorts || [];
  const responseCurrency = data?.currency || 'PKR';
  const labels = cohortRows.map((cohort) => truncateLabel(cohort.display_name));
  const enrollmentColors = cohortRows.map((cohort) => (
    cohort.is_highest_enrollment ? HIGHLIGHT_ENROLLMENT_COLOR : ENROLLMENT_BAR_COLOR
  ));
  const enrollmentBorders = cohortRows.map((cohort) => (
    cohort.is_highest_enrollment ? 3 : 1
  ));
  const revenueColors = cohortRows.map((cohort) => (
    cohort.is_highest_revenue ? HIGHLIGHT_REVENUE_COLOR : REVENUE_BAR_COLOR
  ));
  const revenueBorders = cohortRows.map((cohort) => (
    cohort.is_highest_revenue ? 3 : 1
  ));

  return {
    cohortRows,
    highlights: data?.highlights || null,
    currency: responseCurrency,
    graphData: {
      labels,
      datasets: [
        {
          label: 'Paid Enrollments (Verified)',
          data: cohortRows.map((cohort) => cohort.paid_enrollments),
          backgroundColor: enrollmentColors,
          borderColor: enrollmentColors,
          borderWidth: enrollmentBorders,
          yAxisID: 'y',
        },
        {
          label: 'Realized Revenue',
          data: cohortRows.map((cohort) => cohort.revenue),
          backgroundColor: revenueColors,
          borderColor: revenueColors,
          borderWidth: revenueBorders,
          yAxisID: 'y1',
        },
      ],
    },
  };
};

const EMPTY_COHORTS_RESPONSE = { cohorts: [] };

const TopCohortsGraph = ({ isSuperuser, isStaff }) => {
  const initialState = buildGraphStateFromResponse(
    USE_ANALYTICS_MOCK_DATA ? MOCK_TOP_COHORTS_RESPONSE : EMPTY_COHORTS_RESPONSE,
  );
  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf('month'),
    endDate: moment(),
  });
  const [sortBy, setSortBy] = useState('revenue');
  const [cohorts, setCohorts] = useState(initialState.cohortRows);
  const [highlights, setHighlights] = useState(initialState.highlights);
  const [currency, setCurrency] = useState(initialState.currency);
  const [graphData, setGraphData] = useState(initialState.graphData);

  const applyCohortResponse = (data) => {
    const nextState = buildGraphStateFromResponse(data);
    setCohorts(nextState.cohortRows);
    setHighlights(nextState.highlights);
    setCurrency(nextState.currency);
    setGraphData(nextState.graphData);
  };

  const fetchTopCohorts = async (start, end, sort) => {
    if (USE_ANALYTICS_MOCK_DATA) {
      applyCohortResponse(MOCK_TOP_COHORTS_RESPONSE);
      return;
    }

    try {
      const params = {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        sortBy: sort,
        limit: 10,
      };

      const { data } = await axios({
        withCredentials: true,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': localStorage.getItem('lms-csrf-token'),
        },
        url: `${LMS_BASE_URL}adminpanel/analytics/top-cohorts/`,
        params,
      });

      applyCohortResponse(data?.cohorts?.length ? data : EMPTY_COHORTS_RESPONSE);
    } catch (error) {
      applyCohortResponse(EMPTY_COHORTS_RESPONSE);
    }
  };

  useEffect(() => {
    fetchTopCohorts(dateRange.startDate, dateRange.endDate, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, sortBy]);

  const chartOptions = useMemo(() => getDefaultChartOptions({
    plugins: {
      legend: {
        position: 'top',
        labels: { boxWidth: 12, padding: 15 },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items) => {
            const [{ dataIndex, label }] = items;
            return cohorts[dataIndex]?.display_name || label;
          },
          label: (context) => {
            const { dataIndex, datasetIndex } = context;
            const cohort = cohorts[dataIndex];
            if (!cohort) return context.formattedValue;

            if (datasetIndex === 0) {
              return `Paid Enrollments: ${cohort.paid_enrollments}`;
            }
            return `Realized Revenue: ${formatCurrency(cohort.revenue, cohort.currency || currency)}`;
          },
          afterBody: (items) => {
            const [{ dataIndex }] = items;
            const cohort = cohorts[dataIndex];
            if (!cohort) return [];

            const lines = [
              `Course ID: ${cohort.course_id}`,
              `Seat Price: ${formatCurrency(cohort.seat_price, cohort.currency || currency)}`,
            ];
            if (cohort.is_highest_revenue) lines.push('★ Highest revenue cohort');
            if (cohort.is_highest_enrollment) lines.push('★ Highest paid-enrollment cohort');
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { autoSkip: false, maxRotation: 45, minRotation: 25 },
      },
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        title: { display: true, text: 'Paid Enrollments' },
      },
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) => formatCurrency(value, currency),
        },
        title: { display: true, text: `Revenue (${currency})` },
      },
    },
  }), [cohorts, currency]);

  const handleApply = (event, picker) => {
    setDateRange({
      startDate: picker.startDate,
      endDate: picker.endDate,
    });
  };

  const downloadCSV = () => {
    if (!cohorts.length) return;

    const timestamp = moment().format('YYYY-MM-DD_HH-mm');
    const header = [
      'Cohort Name',
      'Course ID',
      'Paid Enrollments',
      'Seat Price',
      'Revenue',
      'Currency',
    ].join(',');
    const rows = cohorts.map((cohort) => [
      `"${cohort.display_name.replace(/"/g, '""')}"`,
      cohort.course_id,
      cohort.paid_enrollments,
      cohort.seat_price,
      cohort.revenue,
      cohort.currency || currency,
    ].join(','));

    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `top_cohorts_${timestamp}.csv`;
    link.click();
  };

  if (!(isSuperuser || isStaff) && !USE_ANALYTICS_MOCK_DATA) {
    return null;
  }

  if (!USE_ANALYTICS_MOCK_DATA && graphData.labels.length === 0) {
    return null;
  }

  return (
    <div className="analytics-chart-block">
      <h2>Top Ongoing Cohorts</h2>
      <p>
        Verified-track paid enrollments and realized revenue per course run (cohort) for the selected period.
      </p>

      {highlights && (
        <div className="analytics-stats-row cols-2">
          {[
            {
              id: 'highest-revenue',
              label: 'Highest Revenue Cohort',
              cohort: highlights.highest_revenue,
              value: highlights.highest_revenue?.revenue,
              isCurrency: true,
              tooltip: 'Cohort with the highest realized revenue in the selected period.',
            },
            {
              id: 'highest-enrollment',
              label: 'Highest Paid-Enrollment Cohort',
              cohort: highlights.highest_enrollment,
              value: highlights.highest_enrollment?.paid_enrollments,
              isCurrency: false,
              tooltip: 'Cohort with the most verified-track paid enrollments in the selected period.',
            },
          ].map((stat) => (
            <Block key={stat.id} style={{ textAlign: 'center' }}>
              <span className="numbers">
                {renderHighlightStatValue(stat, currency)}
              </span>
              <span className="text-info" style={{ display: 'block', marginTop: '5px' }}>
                {stat.label}
                <OverlayTrigger
                  placement="bottom"
                  overlay={(<Tooltip id={`tooltip-${stat.id}`}>{stat.tooltip}</Tooltip>)}
                >
                  <i className="icon-Help-Outline" style={{ marginLeft: '5px' }}>
                    <span className="sr-only">help</span>
                  </i>
                </OverlayTrigger>
              </span>
              {stat.cohort && (
                <span
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    marginTop: '4px',
                    color: '#555',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.35,
                  }}
                >
                  {stat.cohort.display_name}
                </span>
              )}
            </Block>
          ))}
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-chart-controls">
          <div>
            <label><strong>Date Range:</strong></label>
            <DateRangePicker
              initialSettings={{
                startDate: dateRange.startDate.toDate(),
                endDate: dateRange.endDate.toDate(),
                maxDate: moment().toDate(),
                ranges: {
                  'This Month': [moment().startOf('month'), moment()],
                  'Last Month': [
                    moment().subtract(1, 'month').startOf('month'),
                    moment().subtract(1, 'month').endOf('month'),
                  ],
                  'Last 3 Months': [moment().subtract(3, 'month').startOf('month'), moment()],
                  'Last 6 Months': [moment().subtract(6, 'month').startOf('month'), moment()],
                  'Last Year': [moment().subtract(1, 'year'), moment()],
                },
              }}
              onApply={handleApply}
            >
              <input
                type="text"
                className="form-control"
                value={
                  `${dateRange.startDate.format('MM/DD/YYYY')} - ${dateRange.endDate.format('MM/DD/YYYY')}`
                }
                readOnly
              />
            </DateRangePicker>
          </div>

          <div>
            <label><strong>Rank By:</strong></label>
            <select className="form-control" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="revenue">Revenue</option>
              <option value="enrollments">Paid Enrollments</option>
            </select>
          </div>

          <Button variant="primary" onClick={downloadCSV} disabled={!cohorts.length}>
            <span className="icon-Download-Outline-Color icon" /> Download Report
          </Button>
        </div>

        <div className="analytics-chart-legend">
          <span>
            <span
              style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: HIGHLIGHT_REVENUE_COLOR,
                marginRight: '6px',
                verticalAlign: 'middle',
              }}
            />
            Highest revenue cohort
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: HIGHLIGHT_ENROLLMENT_COLOR,
                marginRight: '6px',
                verticalAlign: 'middle',
              }}
            />
            Highest paid-enrollment cohort
          </span>
        </div>

        <AnalyticsChartContainer>
          {({ width, height }) => (
            <Bar
              key={`${Math.round(width)}x${Math.round(height)}`}
              width={width}
              height={height}
              data={graphData}
              options={chartOptions}
            />
          )}
        </AnalyticsChartContainer>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    isSuperuser: state.LOGGEDINUSER.isSuperuser,
    isStaff: state.LOGGEDINUSER.isStaff,
  };
}

export default connect(mapStateToProps)(TopCohortsGraph);
